import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ArrowDown, ShieldCheck, ScrollText, Video, Clock, MessageSquare,
  History, Plus, Minus, Megaphone, AlertTriangle, Check,
} from 'lucide-react';
import SEO from '../components/SEO';
import Img from '../components/Img';

const SIGNAL_BG = 'https://static.prod-images.emergentagent.com/jobs/78d6f77c-7a7f-48ae-b71b-ec8e8809cfb7/images/c6786a879af9069bce5e7ce80c554929535451cbd28c7da0f3ca7036d7dd8390.png';

// Six scoring signals — weighted to total 100
const SIGNALS = [
  {
    key: 'business',
    n: '01',
    icon: ShieldCheck,
    title: 'Business Verification',
    weight: 18,
    summary: 'Is the company a real, registered legal entity?',
    detail: 'We validate business registration documents, tax IDs, and incorporation records against public registries in the supplier\'s country. Active, in-good-standing registrations score highest.',
    examples: ['Active registration in country of operation', 'Verified tax/VAT identifier', 'No public regulatory infractions on record'],
  },
  {
    key: 'certifications',
    n: '02',
    icon: ScrollText,
    title: 'Certification Evidence',
    weight: 22,
    summary: 'Documented compliance and standards.',
    detail: 'Verified certifications carry the highest weight in the score because they require third-party audit. We check expiry dates, issuing-body validity, and scope of audit.',
    examples: ['ISO 9001 · GOTS · OEKO-TEX · WRAP · BSCI · SMETA', 'Sustainability certifications (GRS, Fair Trade)', 'Certificate within validity window'],
  },
  {
    key: 'media',
    n: '03',
    icon: Video,
    title: 'Factory Media & Video Proof',
    weight: 16,
    summary: 'Real visual evidence of the production facility.',
    detail: 'Verified factory tour videos, dated photographs, and machinery imagery reviewed by our intelligence team. Stock photography is rejected.',
    examples: ['Dated factory walk-through video', 'Photos of machinery in operation', 'Worker-area documentation (where consented)'],
  },
  {
    key: 'response',
    n: '04',
    icon: Clock,
    title: 'Response Rate',
    weight: 14,
    summary: 'How fast and consistently the factory replies to enquiries.',
    detail: 'Computed from 90-day rolling response data on RFQs and buyer messages submitted through Giwahs. Faster, more consistent replies score higher.',
    examples: ['Average response time under 8 hours', '>90% reply rate across enquiries', 'No abandoned conversation threads'],
  },
  {
    key: 'feedback',
    n: '05',
    icon: MessageSquare,
    title: 'Buyer Feedback',
    weight: 14,
    summary: 'Aggregate signals from buyers who have engaged.',
    detail: 'Anonymous post-RFQ and post-engagement signals from buyers, weighted by transaction value indicators. Only verified buyers can submit feedback.',
    examples: ['Verified-buyer ratings', 'Repeat engagement signals', 'Disputes raised + resolved'],
  },
  {
    key: 'history',
    n: '06',
    icon: History,
    title: 'Platform Review History',
    weight: 16,
    summary: 'How long the supplier has been verified and re-audited.',
    detail: 'Suppliers re-verified across multiple cycles build a longer trust history. Recent badge revocations or extended unresponsive periods reduce the signal.',
    examples: ['Verified continuously for 12+ months', 'Successfully re-audited in latest cycle', 'No recent badge revocations'],
  },
];

// Mock example factory score breakdown (fictional)
const EXAMPLE = {
  name: 'Northgate Apparel Collective',
  slug: 'northgate-apparel-collective',
  total: 94,
  breakdown: {
    business: 18,
    certifications: 21,
    media: 15,
    response: 13,
    feedback: 12,
    history: 15,
  },
};

const TIERS = [
  { range: '90–100', label: 'Elite', tone: 'bg-black text-white', desc: 'Top-tier verification across all six signals. Consistently high response, strong audit record.' },
  { range: '80–89', label: 'Trusted', tone: 'bg-white text-black border border-black', desc: 'Strong verification baseline with proven performance. Most established suppliers fall here.' },
  { range: '70–79', label: 'Verified Baseline', tone: 'bg-neutral-200 text-black', desc: 'Core verification complete; building track record on response and feedback signals.' },
  { range: '<70', label: 'Emerging', tone: 'bg-neutral-100 text-neutral-700', desc: 'New to the platform or with limited evidence. Treat with appropriate buyer diligence.' },
];

const FAQS = [
  {
    q: 'How often is the Trust Score recalculated?',
    a: 'Weekly. We recompute scores every Monday at 00:00 UTC. Newly added documentation, certifications, or audit reports surface in the next cycle.',
  },
  {
    q: 'Does paying for Featured placement raise my Trust Score?',
    a: 'No. Featured placement is a paid promotional surface — it has zero influence on Trust Score, badge assignment, or position in the trust-sorted directory. The systems are independently administered.',
  },
  {
    q: 'Why are some categories weighted higher than others?',
    a: 'Certifications carry the highest weight (22) because they require third-party audit and are the hardest signal to fake. Response rate and buyer feedback are weighted slightly lower (14 each) because they capture shorter-term performance.',
  },
  {
    q: 'Can my Trust Score go down?',
    a: 'Yes. Expired certifications, declining response rates, revoked badges, or substantiated buyer disputes will lower the score in the next weekly cycle. Suppliers receive a notice with the specific signal affected.',
  },
  {
    q: 'How do I improve my Trust Score?',
    a: 'Submit current third-party audit reports (highest leverage), upload dated facility media, maintain a >90% response rate under 8 hours, and engage consistently with RFQs you receive. Re-verification cycles also strengthen the history signal over time.',
  },
  {
    q: 'Is the Trust Score a guarantee that I will receive good products?',
    a: 'No. The Trust Score is an informational signal aggregated from public and platform data. It is not a guarantee of performance, product quality, or legal compliance. Buyers must conduct their own due diligence before contracting.',
  },
  {
    q: 'How is buyer feedback collected?',
    a: 'Only verified buyers who have engaged through the platform can submit feedback, and feedback is aggregated anonymously into the signal. Individual reviews are not exposed publicly on supplier profiles.',
  },
  {
    q: 'What if I disagree with my score?',
    a: 'Suppliers can request a review by contacting our intelligence team. We will re-examine the underlying signals and any disputed records. Adjustments, if warranted, take effect in the next weekly recalculation.',
  },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-neutral-200" data-testid={`ts-faq-item-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-start justify-between gap-4 py-5 text-left"
        data-testid={`ts-faq-toggle-${idx}`}
      >
        <span className="font-display text-lg leading-tight tracking-tight md:text-xl" style={{ fontWeight: 600 }}>{q}</span>
        {open ? <Minus size={18} strokeWidth={1.5} className="mt-1 shrink-0" /> : <Plus size={18} strokeWidth={1.5} className="mt-1 shrink-0" />}
      </button>
      {open && <p className="pb-6 text-sm leading-relaxed text-neutral-700">{a}</p>}
    </div>
  );
};

const TrustScorePage = () => {

  return (
    <div data-testid="trust-score-page" className="surface-obsidian text-platinum">
      <SEO
        title="Trust Score 101 — How GIWAHS Scores Factory Trust"
        description="The GIWAHS Trust Score aggregates six independently audited signals: business verification, certification evidence, factory media, response rate, buyer feedback, platform history. Recalculated weekly. Independent of paid Featured placement."
        keywords="factory trust score, supplier verification, GIWAHS trust"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black bg-[#0A0A0A] text-white">
        <div className="absolute inset-0 opacity-40">
          <Img src={SIGNAL_BG} alt="" variant="hero" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-32">
          <div className="flex items-center gap-3 label-overline text-neutral-300">
            <span className="h-px w-10 bg-white" />
            Sourcing Intelligence · Trust Score 101
          </div>
          <h1 className="mt-7 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 8vw, 7rem)', lineHeight: 0.9 }}>
            HOW WE SCORE<br />FACTORY TRUST.
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-neutral-300 md:text-lg">
            The Giwahs Trust Score is an aggregate of six independently audited signals. It is recalculated weekly and entirely separate from paid Featured placement.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#signals" data-testid="ts-hero-signals-cta" className="btn-giwahs btn-giwahs-dark inline-flex items-center gap-2">
              See the six signals <ArrowDown size={14} strokeWidth={1.5} />
            </a>
            <Link to="/factories" data-testid="ts-hero-browse-cta" className="btn-giwahs border border-white/30 bg-transparent text-white hover:bg-white hover:text-black inline-flex items-center gap-2">
              Browse Verified Factories <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* INDEPENDENCE CALLOUT — placed early so the message lands immediately */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <div className="border border-black bg-white p-5 md:p-7" data-testid="ts-independence-notice">
            <div className="flex items-start gap-4">
              <Megaphone size={20} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <div>
                <div className="label-overline">Independence Notice</div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-800 md:text-base">
                  Trust Score is <strong className="text-black">administered independently</strong> from Featured Supplier placements. Paying for a Featured tier does <strong className="text-black">not</strong> raise a supplier's score, unlock verification badges, or change their position in the regular trust-sorted directory. The two systems are deliberately separated to preserve credibility for buyers.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to="/for-suppliers#tiers" className="label-overline border-b border-black pb-0.5 hover:text-neutral-600">
                    About Featured placements →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIX SIGNALS — overview row with weights */}
      <section id="signals" className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="label-overline">/ 01 — The Six Signals</span>
              <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                What the score is built on.
              </h2>
              <p className="mt-4 max-w-xl text-neutral-700">
                Six weighted signals, totalling 100 points. Higher-weight signals are harder to fake — third-party verification and documentation dominate.
              </p>
            </div>
            <span className="font-mono-data text-xs text-neutral-500">WEIGHTS TOTAL: 100</span>
          </div>

          {/* Weight bar */}
          <div className="mt-12">
            <div className="flex h-12 w-full overflow-hidden border border-black" data-testid="ts-weight-bar">
              {SIGNALS.map((s, i) => (
                <div
                  key={s.key}
                  data-testid={`ts-weight-bar-${s.key}`}
                  className={`flex items-center justify-center border-r border-black/20 last:border-r-0 ${i % 2 === 0 ? 'bg-[#0A0A0A] text-white' : 'bg-white text-black'}`}
                  style={{ width: `${s.weight}%` }}
                  title={`${s.title} — ${s.weight}%`}
                >
                  <span className="font-mono-data text-[10px] tracking-wider">{s.weight}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
              {SIGNALS.map((s) => (
                <div key={s.key} className="flex items-center gap-2 font-mono-data text-[11px] text-neutral-600">
                  <span className="inline-block h-2 w-2 bg-black" />
                  {s.title}
                </div>
              ))}
            </div>
          </div>

          {/* Detailed signal cards */}
          <div className="mt-14 grid gap-px bg-black sm:grid-cols-2 lg:grid-cols-3">
            {SIGNALS.map((s) => (
              <div key={s.key} className="bg-white p-6 md:p-7" data-testid={`ts-signal-${s.key}`}>
                <div className="flex items-start justify-between">
                  <s.icon size={20} strokeWidth={1.5} />
                  <div className="text-right">
                    <div className="font-mono-data text-[10px] text-neutral-500">{s.n}</div>
                    <div className="font-display text-2xl leading-none tracking-tighter" style={{ fontWeight: 700 }}>{s.weight}<span className="text-sm font-mono-data text-neutral-500">/100</span></div>
                  </div>
                </div>
                <div className="mt-6 font-display text-xl leading-tight tracking-tight md:text-2xl" style={{ fontWeight: 600 }}>{s.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-700">{s.summary}</p>
                <p className="mt-4 text-xs leading-relaxed text-neutral-600">{s.detail}</p>
                <ul className="mt-5 space-y-2 border-t border-neutral-200 pt-4">
                  {s.examples.map((ex, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs leading-relaxed text-neutral-700">
                      <Check size={12} strokeWidth={1.6} className="mt-0.5 shrink-0" />
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISUAL SCORE BREAKDOWN — example factory */}
      <section className="border-b border-black bg-[#0A0A0A] text-white grain-overlay">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="label-overline text-neutral-400">/ 02 — Worked Example</span>
              <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                A worked example.
              </h2>
              <p className="mt-4 max-w-xl text-neutral-300">
                Illustrative breakdown for an Elite-tier factory. Numbers below are reference figures — used to show how the score composes, not to represent a specific supplier.
              </p>
            </div>
            <span className="font-mono-data text-[11px] text-neutral-500">ILLUSTRATIVE · {EXAMPLE.name.toUpperCase()}</span>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-12">
            {/* Score card */}
            <div className="border border-white/20 p-6 md:col-span-4 md:p-8" data-testid="ts-example-card">
              <div className="label-overline text-neutral-400">Giwahs Trust Score</div>
              <div className="mt-3 flex items-end gap-3">
                <div className="font-display leading-none tracking-tighter" style={{ fontSize: 'clamp(4rem, 8vw, 7rem)', fontWeight: 700 }}>{EXAMPLE.total}</div>
                <div className="pb-3 font-mono-data text-sm text-neutral-500">/100</div>
              </div>
              <div className="mt-4 h-1.5 w-full bg-white/10">
                <div className="h-full bg-white" style={{ width: `${EXAMPLE.total}%` }} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="label-overline inline-block rounded-full border border-white bg-white px-3 py-1 text-black">Elite</span>
                <span className="font-mono-data text-[10px] text-neutral-500">ILLUSTRATIVE</span>
              </div>
              <Link
                to={`/supplier/${EXAMPLE.slug}`}
                data-testid="ts-example-view"
                className="btn-giwahs btn-giwahs-dark mt-7 inline-flex w-full items-center justify-center gap-2"
              >
                View supplier profile <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>

            {/* Breakdown bars */}
            <div className="border border-white/20 p-6 md:col-span-8 md:p-8">
              <div className="label-overline text-neutral-400">Signal-by-signal contribution</div>
              <div className="mt-6 space-y-5">
                {SIGNALS.map((s) => {
                  const earned = EXAMPLE.breakdown[s.key];
                  const pct = (earned / s.weight) * 100;
                  return (
                    <div key={s.key} data-testid={`ts-breakdown-${s.key}`}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <s.icon size={14} strokeWidth={1.5} className="text-neutral-400" />
                          <span className="font-display tracking-tight" style={{ fontWeight: 600 }}>{s.title}</span>
                        </div>
                        <div className="font-mono-data text-xs">
                          <span className="text-white">{earned}</span>
                          <span className="text-neutral-500"> / {s.weight}</span>
                        </div>
                      </div>
                      <div className="mt-2 h-2 w-full bg-white/10">
                        <div className="h-full bg-white" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-7 grid gap-3 border-t border-white/20 pt-5 font-mono-data text-xs text-neutral-400 sm:grid-cols-3">
                <div>WEEKLY RECOMPUTE</div>
                <div>SOURCED FROM 6 SIGNALS</div>
                <div>RANGE: 0–100</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCORE TIERS */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 03 — Tier Bands</span>
          <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
            How buyers read the score.
          </h2>
          <div className="mt-10 grid gap-px bg-black sm:grid-cols-2 lg:grid-cols-4">
            {TIERS.map((t) => (
              <div key={t.label} className="bg-white p-6" data-testid={`ts-tier-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="font-mono-data text-xs text-neutral-500">{t.range}</div>
                <div className="mt-3"><span className={`label-overline rounded-full border px-3 py-1 ${t.tone}`}>{t.label}</span></div>
                <p className="mt-5 text-sm leading-relaxed text-neutral-700">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] grid gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-4">
            <span className="label-overline">/ 04 — FAQ</span>
            <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
              Trust Score, answered.
            </h2>
            <p className="mt-5 text-neutral-700">If something isn't covered here, reach out — we respond within one business day.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-1 border-b border-black pb-0.5 label-overline">
              Contact us <ArrowUpRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-8" data-testid="ts-faq-section">
            <div className="border-t border-neutral-200">
              {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* LEGAL DISCLAIMER */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <div className="border border-amber-900 bg-amber-50 p-6 md:p-8" data-testid="ts-legal-disclaimer">
            <div className="flex items-start gap-4">
              <AlertTriangle size={22} strokeWidth={1.5} className="mt-1 shrink-0 text-amber-900" />
              <div className="text-sm leading-relaxed text-amber-900 md:text-base">
                <strong className="font-semibold uppercase tracking-wider">Legal disclaimer.</strong>{' '}
                The Giwahs Trust Score is an <strong>informational indicator</strong> aggregated from publicly available data, supplier-submitted documentation, third-party certifications, and platform-derived performance signals. It is <strong>not a guarantee</strong> of product quality, on-time delivery, ethical sourcing compliance, legal compliance, financial solvency, or any specific commercial outcome. Trust Score is provided as a sourcing intelligence reference only. Buyers are responsible for conducting their own independent due diligence — including factory audits, sample evaluation, contractual safeguards, and legal review — before engaging any supplier. All scores, badges, and example breakdowns shown on this page are <em>illustrative</em> and used to demonstrate how the score is composed.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — Apply for Verification + Browse Verified */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-32">
          <div className="grid gap-12 md:grid-cols-2">
            <div className="border border-white/30 p-7 md:p-10" data-testid="ts-cta-apply-block">
              <span className="label-overline text-neutral-400">For factories</span>
              <h3 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 0.95 }}>
                Want a higher score?
              </h3>
              <p className="mt-5 text-neutral-400">
                Apply for verification, submit third-party audit documentation, and start building your platform review history. Free to apply.
              </p>
              <Link to="/for-suppliers#apply" data-testid="ts-cta-apply" className="btn-giwahs btn-giwahs-dark mt-8 inline-flex items-center gap-2">
                Apply for Verification <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>

            <div className="border border-white/30 p-7 md:p-10" data-testid="ts-cta-browse-block">
              <span className="label-overline text-neutral-400">For buyers</span>
              <h3 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 0.95 }}>
                Want to source from elite factories?
              </h3>
              <p className="mt-5 text-neutral-400">
                Browse verified factories sorted by Trust Score. Filter by certifications, country, MOQ, and supplier type to find your match.
              </p>
              <Link to="/factories" data-testid="ts-cta-browse" className="btn-giwahs btn-giwahs-dark mt-8 inline-flex items-center gap-2">
                Browse Verified Factories <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrustScorePage;
